/**
 * Shared utilities for handling aliases in ComboBox components
 */

export interface AliasConfig {
  aliases?: Record<string, string>; // alias -> value mapping
}

/**
 * Filters options based on query, with support for alias matching
 * If query exactly matches an alias (case-insensitive), filters to only that value
 * Otherwise performs normal text filtering
 */
export function filterOptionsWithAliases<T extends { label: string; value: any }>(
  options: T[],
  query: string,
  aliases?: Record<string, string>
): T[] {
  if (!query) return options;
  
  // Check if query exactly matches an alias (case-insensitive)
  const aliasMatch = aliases && aliases[query.toUpperCase()];
  if (aliasMatch) {
    // Show only the option that matches the alias
    return options.filter(opt => opt.value === aliasMatch);
  }
  
  // Normal filtering based on query
  return options.filter(option =>
    option.label.toLowerCase().includes(query.toLowerCase()) ||
    (typeof option.value === "string" &&
      option.value.toLowerCase().includes(query.toLowerCase()))
  );
}

/**
 * Builds a reverse mapping from values to their aliases for display purposes
 * Used to show alias hints in dropdown options
 */
export function buildValueToAliasesMap(aliases?: Record<string, string>): Record<string, string[]> {
  const valueToAliases: Record<string, string[]> = {};
  if (aliases) {
    for (const [alias, value] of Object.entries(aliases)) {
      if (!valueToAliases[value]) {
        valueToAliases[value] = [];
      }
      valueToAliases[value].push(alias);
    }
  }
  return valueToAliases;
}

/**
 * Checks if a query string exactly matches any alias (case-insensitive)
 */
export function isExactAliasMatch(query: string, aliases?: Record<string, string>): string | null {
  if (!aliases || !query) return null;
  return aliases[query.toUpperCase()] || null;
}

/**
 * Gets all aliases for a specific value
 */
export function getAliasesForValue(value: string, aliases?: Record<string, string>): string[] {
  if (!aliases) return [];
  return Object.entries(aliases)
    .filter(([_, val]) => val === value)
    .map(([alias, _]) => alias);
}